import "./BottomNavBar.scss";

import clsx from "clsx";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { Link, useMatch } from "react-router";

import { AttentionBubble } from "@/components/Interface/AttentionBubble/AttentionBubble";
import { FocusRing } from "@/components/Layout/BaseLayouts/Footer/FocusRing";
import { usePortalShellConfig } from "@/components/Layout/PortalShellConfig";
import { useScrollDirectionContext } from "@/provider/ScrollDirectionProvider/ScrollDirectionProvider";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { KEYBOARD_TAB_INDEX } from "@/utils/keyboardTabIndex";

const BottomNavItem = ({
  icon,
  activeIcon,
  text,
  target,
  showAttentionBubble,
}: {
  icon: JSX.Element;
  activeIcon: JSX.Element;
  text: string;
  target: string;
  showAttentionBubble?: boolean;
}) => {
  const match = useMatch(target + "/*");

  return (
    <li className={clsx("bottom-nav-item", !!match && "active")}>
      <Link
        className="group relative focus:outline-none"
        to={target}
        tabIndex={KEYBOARD_TAB_INDEX}
        aria-current={match ? "page" : undefined}
      >
        {/* First child: the label is styled as the link's `span:last-child`. */}
        <FocusRing />
        <span className="relative inline-flex">
          {match ? activeIcon : icon}
          {showAttentionBubble && <AttentionBubble />}
        </span>
        <span>{text}</span>
      </Link>
    </li>
  );
};

export const BottomNavBar = () => {
  const { t } = useTranslation();
  const { navigationItems, showFriendsWithBenefitsAttentionBubble } =
    usePortalShellConfig();
  const { atTop } = useScrollDirectionContext();

  return (
    <nav
      id="bottom-nav-bar"
      className={clsx(
        "desktop:hidden",
        // Shrinks into a floating rounded pill once scrolled away from the
        // top -- same treatment as the mobile Header.
        !atTop && "bottom-nav-bar--floating",
      )}
      aria-label={t("layout.menu.bottomNav.ariaLabel")}
    >
      <ul>
        {navigationItems.map((item, index) => {
          const isFriendsWithBenefits =
            item.target === portalRoutes.friendsWithBenefits.base;
          const navigationTextKey = item.bottomNavText ?? item.text;

          return (
            <BottomNavItem
              key={index}
              icon={item.icon}
              activeIcon={item.activeIcon}
              text={t(navigationTextKey)}
              target={item.target}
              showAttentionBubble={
                isFriendsWithBenefits && showFriendsWithBenefitsAttentionBubble
              }
            />
          );
        })}
      </ul>
    </nav>
  );
};
